import axios from 'axios';
import { Helmet } from 'react-helmet';
import { Sanctum } from 'react-sanctum';
import { ToastContainer } from 'react-toastify';
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom'

import './styles/carbon.css'
import "tw-elements-react/dist/css/tw-elements-react.min.css";
import "react-toastify/dist/ReactToastify.css"
import './styles/App.css';
import './styles/global.css';
import './styles/toast.css';
import './styles/react-day-picker.css';
import './resources/fontawesome/css/all.css'

import Config from './api/Config';
import { AUTH, SANCTUM } from './api/Registry';
import { PageNotFound } from './views/errors/PageNotFound';

import { errorRoutes } from './routes/error';
import { patronRoutes } from './routes/patron';
import { temporaryRoutes } from './routes/temp';
import { standardRoutes } from './routes/standard';
import { identityRoutes, trustRoutes } from './routes/identity';

import AuthGuard from './lib/guards/AuthGuard';
import TrustGuard from './lib/guards/TrustGuard';
import ErrorsGuard from './lib/guards/ErrorsGuard';
import PatronGuard from './lib/guards/PatronGuard';
import StandardGuard from './lib/guards/StandardGuard';
import { Thematic } from './components/utils/Thematic';
import AssetGuard from './lib/guards/AssetGuard';
import { assetRoutes } from './routes/assets';
import FreeFormGuard from './lib/guards/FreeFormGuard';
import { freeFormRoutes } from './routes/freeform';
import { readFromCookie, writeToCookie } from './utils/Helpers';
import { COOKIE_KEYS } from './utils/Constants';

interface RouteContextType {
    currentpage: string,
    from: string
}

const RoutingContext = React.createContext<RouteContextType>(null!)

export default function App() {
    const axiosInstance = axios.create({
        headers: {
            'content-type': 'application/json',
        },
    });

    const sanctumConfig = {
        apiUrl: Config.APP_CORE,
        userObjectRoute: SANCTUM._USER,
        csrfCookieRoute: SANCTUM._COOKIE,
        signInRoute: AUTH.SS1_BENEFACTORS,
        signOutRoute: SANCTUM._SIGN_OUT,
        axiosInstance: axiosInstance
    }

    const RouterProvider = ({ children }: { children: React.ReactNode }) => {
        const currentLocation = useLocation()
        const [route, setRoute] = useState({
            currentpage: currentLocation.pathname,
            from: ''
        })

        useEffect(() => {
            setRoute((prev) => ({ currentpage: currentLocation.pathname, from: prev.currentpage }))

            // Ensure the PAGE_COUNT cookie exists
            // - If missing, initialize it with a default value (25)
            // - This gives consistent behavior for pagination across the app,
            //   even for first-time visitors
            if (!readFromCookie(COOKIE_KEYS.PAGE_COUNT)) {
                writeToCookie(COOKIE_KEYS.PAGE_COUNT, 25);
            }
        }, [currentLocation])

        return <RoutingContext.Provider value={route}>
            {children}
        </RoutingContext.Provider>
    }

    return (
        <Thematic>
            <Router basename='/'>
                <Sanctum config={sanctumConfig}>
                    <RouterProvider>
                        <ToastContainer />

                        <Helmet>
                            <meta key={'theme-color'} name="theme-color" content="#F97316" />
                            <link rel="preconnect" href="https://fonts.googleapis.com" />
                            <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
                            <link href="https://fonts.googleapis.com/css2?family=Silkscreen:wght@400;700&display=swap" rel="stylesheet" />
                        </Helmet>

                        <Routes>
                            <Route element={<PatronGuard />}>
                                {
                                    patronRoutes.map((route, index) => {
                                        return (
                                            <Route
                                                path={route.path}
                                                element={route.element}
                                                key={'patron-routes-' + index}
                                            />
                                        )
                                    })
                                }
                            </Route>

                            <Route element={<AuthGuard />}>
                                {
                                    identityRoutes.map((route, index) => {
                                        return (
                                            <Route
                                                path={route.path}
                                                element={route.element}
                                                key={'identity-routes-' + index}
                                            />
                                        )
                                    })
                                }
                            </Route>

                            <Route element={<TrustGuard />}>
                                {
                                    trustRoutes.map((route, index) => {
                                        return (
                                            <Route
                                                path={route.path}
                                                element={route.element}
                                                key={'trust-routes-' + index}
                                            />
                                        )
                                    })
                                }
                            </Route>

                            <Route element={<StandardGuard />}>
                                {
                                    standardRoutes.map((route, index) => {
                                        return (
                                            <Route
                                                path={route.path}
                                                element={route.element}
                                                key={'standard-routes-' + index}
                                            />
                                        )
                                    })
                                }
                            </Route>

                            <Route element={<FreeFormGuard />}>
                                {
                                    freeFormRoutes.map((route, index) => {
                                        return (
                                            <Route
                                                path={route.path}
                                                element={route.element}
                                                key={'free-form-routes-' + index}
                                            />
                                        )
                                    })
                                }
                            </Route>

                            <Route element={<AssetGuard />}>
                                {
                                    assetRoutes.map((route, index) => {
                                        return (
                                            <Route
                                                path={route.path}
                                                element={route.element}
                                                key={'asset-routes-' + index}
                                            />
                                        )
                                    })
                                }
                            </Route>

                            <Route element={<ErrorsGuard />}>
                                {
                                    errorRoutes.map((route, index) => {
                                        return (
                                            <Route
                                                path={route.path}
                                                element={route.element}
                                                key={'error-routes-' + index}
                                            />
                                        )
                                    })
                                }
                            </Route>

                            {
                                temporaryRoutes.map((route, index) => {
                                    return (
                                        <Route element={
                                            route.guard === 'auth' ? <AuthGuard /> : null
                                        }>
                                            <Route
                                                path={route.path}
                                                element={route.element}
                                                key={'temp-routes-' + index}
                                            />
                                        </Route>
                                    )
                                })
                            }

                            <Route path="*" element={<PageNotFound />} />

                        </Routes>
                    </RouterProvider>
                </Sanctum>
            </Router>
        </Thematic>
    );
}
